import React, { memo, useCallback, useMemo, useState } from 'react'
import useTranslation from 'next-translate/useTranslation'
import styles from 'tennders-common/form/styles/transactionSelect.module.scss'
import GHref from 'tennders-market/common/components/element/GHref'
import { useDetectClickOutside } from 'react-detect-click-outside'
import { motion } from 'framer-motion'
import { InputLabeledContainer, TextInput } from 'tennders-common/form/components'
import GLoading from 'tennders-market/common/components/element/GLoading'
import { useDebounce } from 'use-debounce'

type Props = {
  fetchItems?: (params?: any) => any,
  disabled?: boolean
  onSet: (item: any) => void,
  error?: any,
  valueKey?: string,
  label?: string,
  labelKey?: string,
  options: any[]
}

type OptionProps = {
  item: any,
  onClick: (item: any) => void,
  labelKey?: string
}

const SelectOption = memo(({ item, onClick, labelKey }: OptionProps) => (
  <div onClick={() => onClick(item)} className={styles.item} key={item[labelKey]}>
    <div className={styles.itemTitle}>
      {item[labelKey]}
    </div>
  </div>
), (prev, next) => JSON.stringify(prev.item) === JSON.stringify(next.item))

const SearchableSelect = (props:Props) => {
  const { disabled, onSet, valueKey, labelKey, label, options } = props
  const { t } = useTranslation()
  // const [options, setOptions] = useState([])
  const [modalOpen, setModalOpen] = useState(false)
  const [search, setSearch] = useState('')
  const [selectedItem, setSelectedItem] = useState(null)

  const [debouncedSearch] = useDebounce(search, 300)

  const closeDropdown = useCallback(() => {
    setSearch('')
    setModalOpen(false)
  }, [])
  const ref = useDetectClickOutside({
    onTriggered: closeDropdown,
    disableClick: true
  })

  const filteredItems = useMemo(() => {
    if (options?.length > 0) {
      if (options?.length > 10 && debouncedSearch) {
        const lowercaseQuery = debouncedSearch.toLowerCase()
        return options.filter(obj => obj[labelKey]?.toLowerCase()?.includes(lowercaseQuery))
      }
      if (options?.length <= 10) {
        return options
      }
    }
    return []
  }, [options, debouncedSearch, labelKey])

  const onSelect = (item) => {
    onSet(item[valueKey])
    setSearch('')
    setSelectedItem(item[labelKey])
    setModalOpen(false)
  }

  return (
    <div className={styles.outerContainer}>
      <div onClick={() => !disabled && setModalOpen(true)}>
        <div className={`smaller form-input form-select w-100${disabled ? ' disabled' : ''}${selectedItem ? ' selected' : ''}`}>
          <div className="d-flex align-center h-100 mr-12">
            {selectedItem || t('common:select')}
          </div>
        </div>
      </div>
      {modalOpen ? (
        <div ref={ref} className={styles.innerContainer}>
          <GHref onClick={() => setModalOpen(false)} className="close-modal"></GHref>
          <motion.div transition={{ duration: 0.2 }} initial={{ scaleY: 0 }} animate={{ scaleY: 1 }} id="scrollableDiv" className={styles.selectContainer}>
            <InputLabeledContainer
              name="search"
              form={{ search }}
              label={label}
              onChange={(_, value) => setSearch(value)}
              renderInput={(props) => <TextInput {...props} />}
            />
            {options && options?.length === 0 && search === ''
              ? (
                <div className={styles.loadignContainer}>
                  <GLoading size={30} />
                </div>
              )
              : null}
            {filteredItems?.length <= 10 && filteredItems.map((item) => <SelectOption onClick={onSelect} key={item.label} item={item} labelKey={labelKey} />)}
            {(filteredItems?.length > 10 && options?.length > 0) && t('searchFilters:found_more_than_ten')}
            {(filteredItems?.length === 0 && options?.length > 0) && (
              search === '' ? t('searchFilters:search_to_load_items') : t('searchFilters:no_matching_item')
            )}
          </motion.div>
        </div>
      ) : null}
    </div>
  )
}

export default SearchableSelect
